
import { of as observableOf,  Observable, Observer } from 'rxjs';
import { Injectable } from '@angular/core';
import { MultiselectProvider } from '../multiselect-provider';
import { MultiselectOptionItem, UiConstants } from '../../util/core-utils';
import { Set } from 'immutable';
import { OrderType, QueryResult } from '../util/services';
import { CompanyLocation, CompanyLocationService } from './company-location.service';

@Injectable()
export class CompanyLocationMultiselectProvider implements MultiselectProvider {

  private multiselectQueryRequest: CompanyLocation.QueryRequest = {
    paging: {
      pageNumber: 1,
      numberOfItems: UiConstants.autocompletePageSize
    },
    noProgressBar: true,
    orders: Set.of({field: CompanyLocation.OrderField.NAME, type: OrderType.ASC}),
    disabled: false,
    name: undefined,
    companyIds: undefined,
  };

  constructor(private companyLocationService: CompanyLocationService) {
  }

  load(queryRequest: CompanyLocation.QueryRequest): Observable<MultiselectOptionItem<number>[]> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>[]>) => {
      this.companyLocationService.query(queryRequest).subscribe((result: QueryResult<CompanyLocation.CompanyLocation>) => {
          observer.next(result.items.map(item => this.toMultiselectOptionItem(item!)).toArray());
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  loadCompanyLocations(): Observable<MultiselectOptionItem<number>[]> {
    return this.load(this.multiselectQueryRequest);
  }

  loadCompanyLocationsWithCompanyIds(companyIds?: Set<number>): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.companyIds = companyIds;
    return this.load(queryRequest);
  }

  searchCompanyLocationsWithCompanyIds(predicate?: string, companyIds?: Set<number>): Observable<MultiselectOptionItem<number>[]> {
    if (!companyIds) {
      const emptyList: MultiselectOptionItem<number>[] = [];
      return observableOf(emptyList);
    }
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.companyIds = companyIds;
    queryRequest.name = predicate;
    return this.load(queryRequest);
  }

  loadAll(predicate?: string): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.name = predicate;
    return this.load(queryRequest);
  }

  getById(id: number): Observable<MultiselectOptionItem<number>> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>>) => {
      this.companyLocationService.get({
        id: id
      }).subscribe((result: CompanyLocation.CompanyLocation) => {
          observer.next(this.toMultiselectOptionItem(result!));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  getByIds(ids: number[]): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign([], this.multiselectQueryRequest);
    queryRequest.id = Set.of(...ids);
    queryRequest.paging = undefined;
    return this.load(queryRequest);
  }

  toMultiselectOptionItem(item: CompanyLocation.CompanyLocation): MultiselectOptionItem<number> {
    const ret = new MultiselectOptionItem<number>();
    ret.id = item.id;
    ret.itemName = item.name;
    ret.disabled = item.disabled;
    return ret;
  }

}
